/**
 * Relative date windows — "this quarter", "last year" — resolved to a
 * concrete `[from, to]` pair at the moment they are used, never when they
 * were chosen. A saved view or a workflow rule stores the token
 * (`THIS_QUARTER`), not a pair of dates, so it means the same thing whenever
 * it next runs — the whole point of "relative".
 *
 * Quarters are calendar quarters (Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec) in UTC:
 * the app has no fiscal-year setting, and every date-only column it compares
 * against (`expectedCloseDate` and friends) is stored and compared in UTC.
 *
 * Shared rather than duplicated in the API and the browser: the picker that
 * shows "01/09/2026 → 30/09/2026" as a preview and the query that actually
 * filters by it must agree, or the preview would lie.
 */

export const RELATIVE_DATE_RANGE_TOKENS = [
  'THIS_MONTH',
  'PREVIOUS_MONTH',
  'THIS_QUARTER',
  'PREVIOUS_QUARTER',
  'NEXT_QUARTER',
  'THIS_YEAR',
  'PREVIOUS_YEAR',
  'Q1',
  'Q2',
  'Q3',
  'Q4',
] as const;
export type RelativeDateRangeToken = (typeof RELATIVE_DATE_RANGE_TOKENS)[number];

export const RELATIVE_DATE_RANGE_LABELS: Record<RelativeDateRangeToken, string> = {
  THIS_MONTH: 'This month',
  PREVIOUS_MONTH: 'Previous month',
  THIS_QUARTER: 'This quarter',
  PREVIOUS_QUARTER: 'Previous quarter',
  NEXT_QUARTER: 'Next quarter',
  THIS_YEAR: 'This year',
  PREVIOUS_YEAR: 'Previous year',
  Q1: 'Q1 (Jan–Mar), this year',
  Q2: 'Q2 (Apr–Jun), this year',
  Q3: 'Q3 (Jul–Sep), this year',
  Q4: 'Q4 (Oct–Dec), this year',
};

export function isRelativeDateRangeToken(value: unknown): value is RelativeDateRangeToken {
  return (
    typeof value === 'string' &&
    (RELATIVE_DATE_RANGE_TOKENS as readonly string[]).includes(value)
  );
}

export interface ResolvedDateRange {
  /** Inclusive start, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  to: string;
}

function isoDate(year: number, monthIndex: number, day: number): string {
  return new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);
}

/** The first and last day of the calendar quarter (0-indexed: 0 = Q1) containing `monthIndex`. */
function quarterBounds(year: number, quarterIndex: number): ResolvedDateRange {
  const startMonth = quarterIndex * 3;
  return {
    from: isoDate(year, startMonth, 1),
    // Day 0 of the month after the quarter's last month is that last month's final day.
    to: isoDate(year, startMonth + 3, 0),
  };
}

/** The first and last day of the given calendar month (0-indexed: 0 = January). */
function monthBounds(year: number, monthIndex: number): ResolvedDateRange {
  return {
    from: isoDate(year, monthIndex, 1),
    // Day 0 of the following month is this month's final day.
    to: isoDate(year, monthIndex + 1, 0),
  };
}

/**
 * Resolves a relative token against `now` (UTC) into a concrete inclusive
 * `[from, to]` range of `YYYY-MM-DD` dates.
 */
export function resolveRelativeDateRange(
  token: RelativeDateRangeToken,
  now: Date = new Date(),
): ResolvedDateRange {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const quarterIndex = Math.floor(month / 3);

  switch (token) {
    case 'THIS_MONTH':
      return monthBounds(year, month);
    case 'PREVIOUS_MONTH':
      return month === 0 ? monthBounds(year - 1, 11) : monthBounds(year, month - 1);
    case 'THIS_QUARTER':
      return quarterBounds(year, quarterIndex);
    case 'PREVIOUS_QUARTER':
      return quarterIndex === 0 ? quarterBounds(year - 1, 3) : quarterBounds(year, quarterIndex - 1);
    case 'NEXT_QUARTER':
      return quarterIndex === 3 ? quarterBounds(year + 1, 0) : quarterBounds(year, quarterIndex + 1);
    case 'THIS_YEAR':
      return { from: isoDate(year, 0, 1), to: isoDate(year, 11, 31) };
    case 'PREVIOUS_YEAR':
      return { from: isoDate(year - 1, 0, 1), to: isoDate(year - 1, 11, 31) };
    case 'Q1':
      return quarterBounds(year, 0);
    case 'Q2':
      return quarterBounds(year, 1);
    case 'Q3':
      return quarterBounds(year, 2);
    case 'Q4':
      return quarterBounds(year, 3);
  }
}
